import { useQueryClient } from '@tanstack/react-query';
import {
  PlayableInvoiceExclusionsSearch,
  usePlayableInvoiceExclusions,
} from '@/enterprise/data/usePlayableInvoiceExclusions';
import { PlayableInvoice } from '@/enterprise/data/usePlayableInvoices';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import {
  useRefetchStatsQueries,
  useServerSideEventListeners,
} from '@/lib/serverSentEvents/useServerSideEventListeners';
import useDataDog from '@/reporting/DataDog';

export interface EditError {
  error: unknown;
  takerMarkets: TakerMarket[];
}

interface InvoiceEditsOptions {
  onLoadingChange?: (loading: boolean) => void;
  onProgressChange?: (current: number, total: number) => void;
  onResetSelection?: () => void;
  getSelectedInvoices?: () => PlayableInvoice[];
  onError?: (errors: EditError[]) => void;
  subscribeToMarketStats?: boolean;
}

const groupTakerMarketsByOrg = (takerMarkets: TakerMarket[]) => {
  return takerMarkets.reduce(
    (acc, takerMarket) => {
      if (!acc[takerMarket.makerOrganizationUuid]) {
        acc[takerMarket.makerOrganizationUuid] = [];
      }

      acc[takerMarket.makerOrganizationUuid].push(takerMarket);
      return acc;
    },
    {} as Record<string, TakerMarket[]>
  );
};

const useInvoiceEdits = (options?: InvoiceEditsOptions) => {
  const { addError } = useDataDog();
  const queryClient = useQueryClient();
  const { mutateAsync } = usePlayableInvoiceExclusions();
  const { refetchManyTakerMarkets } = useRefetchStatsQueries();
  const { subscribeToMarketStats: subscribeToMarketStatsFn } = useServerSideEventListeners();
  const { onLoadingChange, onProgressChange, onResetSelection, getSelectedInvoices, onError } = options ?? {};

  // refetches playable invoices and stats for given taker markets
  const refetchMany = async (takerMarkets: TakerMarket[]) => {
    const makerOrgUuids = Array.from(new Set(takerMarkets.map((tm) => tm.makerOrganizationUuid)));

    await Promise.all([
      ...makerOrgUuids.map((makerOrgUuid) =>
        queryClient.refetchQueries({ queryKey: ['playable-invoices', makerOrgUuid] })
      ),
      ...makerOrgUuids.map((makerOrgUuid) =>
        queryClient.refetchQueries({ queryKey: ['playable-invoice-stats', makerOrgUuid] })
      ),
      // refetch for latest taker market data, includes pricing suggestion invalidation
      refetchManyTakerMarkets(
        takerMarkets.map((takerMarket) => ({
          marketUuid: takerMarket.marketUuid,
          takerId: takerMarket.takerDivisionId,
        }))
      ),
    ]);
  };

  // refetches playable invoices and stats for given taker markets AND resets loading state
  const refetchManyAndReset = async (takerMarkets: TakerMarket[]) => {
    await refetchMany(takerMarkets);
    onLoadingChange?.(false);
  };

  const logErrorsToDatadog = (errors: EditError[], handlerName: string) => {
    const errorsByMakerOrg = errors.reduce(
      (acc, error) => {
        error.takerMarkets.forEach((tm) => {
          if (!acc[tm.makerOrganizationUuid]) {
            acc[tm.makerOrganizationUuid] = { takerMarkets: [], error: error.error };
          }

          acc[tm.makerOrganizationUuid].takerMarkets.push(tm);
        });
        return acc;
      },
      {} as Record<string, { takerMarkets: TakerMarket[]; error: unknown }>
    );

    Object.entries(errorsByMakerOrg).forEach(([makerOrgUuid, { takerMarkets: orgTakerMarkets, error }]) => {
      addError('Enterprise Playable Invoice Exclusions Error', {
        error,
        handler: handlerName,
        makerOrgUuid,
        takerMarkets: orgTakerMarkets.map((tm) => ({
          marketUuid: tm.marketUuid,
          takerUuid: tm.takerDivisionUuid,
        })),
      });
    });
  };

  const processOrganizationMutations = async (
    takerMarkets: TakerMarket[],
    handlerName: string,
    mutationBuilder: (
      makerOrgUuid: string,
      makerOrgTakerMarkets: TakerMarket[],
      isLastOrg: boolean,
      makerOrgIndex: number
    ) => Parameters<typeof mutateAsync>[0]
  ) => {
    const takerMarketsByOrg = groupTakerMarketsByOrg(takerMarkets);
    const makerOrgGroups = Object.entries(takerMarketsByOrg);
    let processedTakerMarkets = 0;
    const successfulTakerMarkets: TakerMarket[] = [];
    const errors: EditError[] = [];

    for (let makerOrgIndex = 0; makerOrgIndex < makerOrgGroups.length; makerOrgIndex++) {
      const [makerOrgUuid, makerOrgTakerMarkets] = makerOrgGroups[makerOrgIndex];
      const isLastOrg = makerOrgIndex === makerOrgGroups.length - 1;

      try {
        await mutateAsync(mutationBuilder(makerOrgUuid, makerOrgTakerMarkets, isLastOrg, makerOrgIndex));
        successfulTakerMarkets.push(...makerOrgTakerMarkets);
      } catch (error) {
        errors.push({ takerMarkets: makerOrgTakerMarkets, error });
      }

      processedTakerMarkets += makerOrgTakerMarkets.length;
      onProgressChange?.(processedTakerMarkets, takerMarkets.length);
    }

    if (errors.length > 0) {
      logErrorsToDatadog(errors, handlerName);
      onError?.(errors);
      await refetchMany(successfulTakerMarkets);
      if (options?.subscribeToMarketStats) {
        successfulTakerMarkets.forEach((tm) => {
          subscribeToMarketStatsFn({
            marketUuid: tm.marketUuid,
            takerId: tm.takerDivisionId,
          });
        });
      }
    } else {
      await refetchManyAndReset(takerMarkets);
      if (options?.subscribeToMarketStats) {
        takerMarkets.forEach((tm) => {
          subscribeToMarketStatsFn({
            marketUuid: tm.marketUuid,
            takerId: tm.takerDivisionId,
          });
        });
      }
    }
  };

  const handleIncludeAllInvoices = async (takerMarkets: TakerMarket[]) => {
    onResetSelection?.();
    onLoadingChange?.(true);
    onProgressChange?.(0, takerMarkets.length);

    await processOrganizationMutations(
      takerMarkets,
      'handleIncludeAllInvoices',
      (makerOrgUuid, makerOrgTakerMarkets, isLastOrg) => ({
        makerOrganizationUuid: makerOrgUuid,
        includeStats: isLastOrg,
        operation: 'INCLUDE',
        takerMarkets: makerOrgTakerMarkets,
      })
    );
  };

  const handleExcludeAllInvoices = async (takerMarkets: TakerMarket[]) => {
    onResetSelection?.();
    onLoadingChange?.(true);
    onProgressChange?.(0, takerMarkets.length);

    await processOrganizationMutations(
      takerMarkets,
      'handleExcludeAllInvoices',
      (makerOrgUuid, makerOrgTakerMarkets, isLastOrg) => ({
        makerOrganizationUuid: makerOrgUuid,
        includeStats: isLastOrg,
        operation: 'EXCLUDE',
        takerMarkets: makerOrgTakerMarkets,
      })
    );
  };

  const handleExclude = async (takerMarkets: TakerMarket[]) => {
    const selectedInvoices = getSelectedInvoices?.() ?? [];
    onResetSelection?.();
    onLoadingChange?.(true);
    onProgressChange?.(0, takerMarkets.length);

    await processOrganizationMutations(
      takerMarkets,
      'handleExclude',
      (makerOrgUuid, makerOrgTakerMarkets, isLastOrg) => ({
        includeStats: isLastOrg,
        makerOrganizationUuid: makerOrgUuid,
        takerMarkets: makerOrgTakerMarkets,
        overrides: {
          exclude: {
            vouchers: selectedInvoices.map((invoice) => invoice.voucherId),
          },
        },
      })
    );
  };

  const handleInclude = async (takerMarkets: TakerMarket[]) => {
    const selectedInvoices = getSelectedInvoices?.() ?? [];
    onResetSelection?.();
    onLoadingChange?.(true);
    onProgressChange?.(0, takerMarkets.length);

    await processOrganizationMutations(
      takerMarkets,
      'handleInclude',
      (makerOrgUuid, makerOrgTakerMarkets, isLastOrg) => ({
        includeStats: isLastOrg,
        makerOrganizationUuid: makerOrgUuid,
        takerMarkets: makerOrgTakerMarkets,
        overrides: {
          include: {
            vouchers: selectedInvoices.map((invoice) => invoice.voucherId),
          },
        },
      })
    );
  };

  const handleIncludeByCriteria = async ({
    takerMarkets,
    search,
    reset,
    operator,
  }: {
    takerMarkets: TakerMarket[];
    search: PlayableInvoiceExclusionsSearch;
    reset?: boolean;
    operator: 'AND' | 'OR';
  }) => {
    onLoadingChange?.(true);
    onProgressChange?.(0, takerMarkets.length);

    // for and, do in one call
    // for or, do in multiple calls
    if (operator === 'AND') {
      await processOrganizationMutations(
        takerMarkets,
        'handleIncludeByCriteria',
        (makerOrgUuid, makerOrgTakerMarkets, isLastOrg) => ({
          includeStats: isLastOrg,
          makerOrganizationUuid: makerOrgUuid,
          operation: 'INCLUDE',
          search,
          takerMarkets: makerOrgTakerMarkets,
          ...(reset && { mode: 'FOCUSING' }),
        })
      );
    } else {
      const takerMarketsByOrg = groupTakerMarketsByOrg(takerMarkets);
      const makerOrgGroups = Object.entries(takerMarketsByOrg);
      let processedTakerMarkets = 0;
      const successfulTakerMarkets: TakerMarket[] = [];
      const errors: EditError[] = [];

      // process each filter in search and make multiple calls
      if (search) {
        const allFilters: Array<{ key: string; filter: unknown }> = [];

        // get all filters
        for (const [key, filters] of Object.entries(search)) {
          if (filters && Array.isArray(filters)) {
            for (const filter of filters) {
              allFilters.push({ key, filter });
            }
          }
        }

        // process each filter
        // only set mode to focusing on the first filter if reset is true
        // only include stats on the last one
        for (let makerOrgIndex = 0; makerOrgIndex < makerOrgGroups.length; makerOrgIndex++) {
          const [makerOrgUuid, makerOrgTakerMarkets] = makerOrgGroups[makerOrgIndex];
          let makerOrgSuccess = true;

          for (let i = 0; i < allFilters.length; i++) {
            const { key, filter } = allFilters[i];
            const isFirstFilter = i === 0;
            const isLastOrg = makerOrgIndex === makerOrgGroups.length - 1;
            const isLastFilter = i === allFilters.length - 1 && isLastOrg;

            try {
              await mutateAsync({
                includeStats: isLastFilter,
                makerOrganizationUuid: makerOrgUuid,
                operation: 'INCLUDE',
                search: { [key]: [filter] },
                takerMarkets: makerOrgTakerMarkets,
                ...(isFirstFilter && reset && { mode: 'FOCUSING' }),
              });
            } catch (error) {
              errors.push({ takerMarkets: makerOrgTakerMarkets, error });
              makerOrgSuccess = false;
              break;
            }
          }

          if (makerOrgSuccess) {
            successfulTakerMarkets.push(...makerOrgTakerMarkets);
          }

          processedTakerMarkets += makerOrgTakerMarkets.length;
          onProgressChange?.(processedTakerMarkets, takerMarkets.length);
        }
      }

      if (errors.length > 0) {
        logErrorsToDatadog(errors, 'handleIncludeByCriteria');
        onError?.(errors);
        await refetchMany(successfulTakerMarkets);
      } else {
        await refetchManyAndReset(takerMarkets);
      }
    }
  };

  return {
    handleExclude,
    handleExcludeAllInvoices,
    handleInclude,
    handleIncludeAllInvoices,
    handleIncludeByCriteria,
  };
};

export default useInvoiceEdits;
